import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText, Video, Image, Upload, Package, Award, Plus, Search,
  MoreHorizontal, Edit, Trash2, Download, Eye, Loader2, Filter,
  Share2, Lock, Tag, HardDrive, ExternalLink, CheckCircle2, Palette,
  SquareAsterisk, FileCheck, LayoutTemplate
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { toast } from "sonner";
import api from "../../lib/api";
import { cn } from "../../lib/utils";

const CONTENT_TYPES = {
  scorm: { label: "SCORM Package", icon: Package, color: "bg-blue-50 text-blue-700 border-blue-200" },
  video: { label: "Video", icon: Video, color: "bg-red-50 text-red-700 border-red-200" },
  document: { label: "Document", icon: FileText, color: "bg-green-50 text-green-700 border-green-200" },
  image: { label: "Image", icon: Image, color: "bg-amber-50 text-amber-700 border-amber-200" },
  certificate: { label: "Certificate", icon: Award, color: "bg-violet-50 text-violet-700 border-violet-200" },
};

const BORDER_STYLES = {
  elegant: "Double line with corners",
  modern: "Clean single border",
  minimal: "No border, spacious",
  ornate: "Decorative with flourishes",
};

const CERT_FIELDS = [
  { key: "learner_name", label: "Learner Name" },
  { key: "course_name", label: "Course Name" },
  { key: "completion_date", label: "Completion Date" },
  { key: "certificate_id", label: "Certificate ID" },
  { key: "score", label: "Score" },
  { key: "hours_completed", label: "Hours Completed" },
  { key: "instructor_name", label: "Instructor Name" },
  { key: "expiry_date", label: "Expiry Date" },
];

const ContentLibraryPage = () => {
  const [activeView, setActiveView] = useState("library");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");

  // Content dialog
  const [showContentDialog, setShowContentDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [contentForm, setContentForm] = useState({ title: "", description: "", content_type: "document", category: "General", file_url: "", file_name: "", file_size_bytes: 0, tags: "", is_shared: true });
  const [saving, setSaving] = useState(false);

  // Certificate dialog
  const [showCertDialog, setShowCertDialog] = useState(false);
  const [editCert, setEditCert] = useState(null);
  const [certForm, setCertForm] = useState({
    name: "", description: "", layout: "landscape", background_color: "#FFFFFF",
    accent_color: "#1a56db", border_style: "elegant", signature_name: "",
    signature_title: "", footer_text: "", include_fields: ["learner_name", "course_name", "completion_date", "certificate_id"],
  });

  const fetchAll = async () => {
    try {
      const [lib, ce] = await Promise.all([
        api.get("/admin/content-library"),
        api.get("/admin/certificate-templates"),
      ]);
      setItems(lib.data || []);
      setCerts(ce.data || []);
    } catch {}
    try { const st = await api.get("/admin/content-library/stats"); setStats(st.data || {}); } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const filtered = items.filter(i =>
    (filterType === "all" || i.content_type === filterType) &&
    (!search || i.title?.toLowerCase().includes(search.toLowerCase()) || i.description?.toLowerCase().includes(search.toLowerCase()))
  );

  const formatSize = (bytes) => {
    if (!bytes) return "0 B";
    const k = 1024; const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  const handleSaveContent = async () => {
    if (!contentForm.title) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      const payload = { ...contentForm, tags: contentForm.tags ? contentForm.tags.split(",").map(t => t.trim()).filter(Boolean) : [] };
      if (editItem) {
        await api.put(`/admin/content-library/${editItem.id}`, payload);
        toast.success("Content updated");
      } else {
        await api.post("/admin/content-library", payload);
        toast.success("Content added");
      }
      setShowContentDialog(false);
      fetchAll();
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const handleDeleteContent = async (id) => {
    if (!window.confirm("Delete this content?")) return;
    try { await api.delete(`/admin/content-library/${id}`); toast.success("Deleted"); fetchAll(); } catch { toast.error("Failed"); }
  };

  const handleSaveCert = async () => {
    if (!certForm.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      if (editCert) {
        await api.put(`/admin/certificate-templates/${editCert.id}`, certForm);
        toast.success("Certificate template updated");
      } else {
        await api.post("/admin/certificate-templates", certForm);
        toast.success("Certificate template created");
      }
      setShowCertDialog(false);
      fetchAll();
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const handleDeleteCert = async (id) => {
    if (!window.confirm("Delete certificate template?")) return;
    try { await api.delete(`/admin/certificate-templates/${id}`); toast.success("Deleted"); fetchAll(); } catch { toast.error("Failed"); }
  };

  const openEditContent = (item) => {
    setEditItem(item);
    setContentForm({ title: item.title, description: item.description || "", content_type: item.content_type, category: item.category || "General", file_url: item.file_url || "", file_name: item.file_name || "", file_size_bytes: item.file_size_bytes || 0, tags: (item.tags || []).join(", "), is_shared: item.is_shared !== false });
    setShowContentDialog(true);
  };

  const openEditCert = (cert) => {
    setEditCert(cert);
    setCertForm({
      name: cert.name, description: cert.description || "", layout: cert.layout || "landscape",
      background_color: cert.background_color || "#FFFFFF", accent_color: cert.accent_color || "#1a56db",
      border_style: cert.border_style || "elegant", signature_name: cert.signature_name || "",
      signature_title: cert.signature_title || "", footer_text: cert.footer_text || "",
      include_fields: cert.include_fields || ["learner_name", "course_name", "completion_date", "certificate_id"],
    });
    setShowCertDialog(true);
  };

  return (
    <AdminLayout title="Content Library">
      <div className="space-y-5 max-w-7xl" data-testid="content-library-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold" data-testid="content-library-title">Content Library & Certificates</h1>
            <p className="text-sm text-muted-foreground">Manage master content repository and certificate templates</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant={activeView === "library" ? "default" : "outline"} size="sm" className="text-xs h-8 gap-1" onClick={() => setActiveView("library")} data-testid="view-library-btn">
              <HardDrive className="w-3.5 h-3.5" /> Content Library
            </Button>
            <Button variant={activeView === "certificates" ? "default" : "outline"} size="sm" className="text-xs h-8 gap-1" onClick={() => setActiveView("certificates")} data-testid="view-certs-btn">
              <Award className="w-3.5 h-3.5" /> Certificate Templates
            </Button>
          </div>
        </div>

        {loading ? <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div> : activeView === "library" ? (
          <div className="space-y-4">
            {/* Stats */}
            {stats && (
              <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
                <Card className="border shadow-none"><CardContent className="p-3 flex items-center gap-2.5">
                  <HardDrive className="w-4 h-4 text-primary" /><div><p className="text-lg font-bold">{stats.total || 0}</p><p className="text-[10px] text-muted-foreground">Total Items</p></div>
                </CardContent></Card>
                {Object.entries(CONTENT_TYPES).map(([key, cfg]) => (
                  <Card key={key} className="border shadow-none"><CardContent className="p-3 flex items-center gap-2.5">
                    <cfg.icon className={cn("w-4 h-4", cfg.color.split(" ")[1])} /><div><p className="text-lg font-bold">{stats[key]?.count || 0}</p><p className="text-[10px] text-muted-foreground">{cfg.label}</p></div>
                  </CardContent></Card>
                ))}
              </div>
            )}

            {/* Filters */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input placeholder="Search content..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-8 text-xs w-48" data-testid="search-content" />
                </div>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="h-8 text-xs w-36"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {Object.entries(CONTENT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button size="sm" className="text-xs h-8 gap-1" onClick={() => { setEditItem(null); setContentForm({ title: "", description: "", content_type: "document", category: "General", file_url: "", file_name: "", file_size_bytes: 0, tags: "", is_shared: true }); setShowContentDialog(true); }} data-testid="add-content-btn">
                <Plus className="w-3.5 h-3.5" /> Add Content
              </Button>
            </div>

            {/* Grid */}
            {filtered.length === 0 ? (
              <Card className="border shadow-none"><CardContent className="py-16 text-center">
                <HardDrive className="w-12 h-12 mx-auto text-muted-foreground/20 mb-3" />
                <p className="text-sm font-medium">No content yet</p>
                <p className="text-xs text-muted-foreground mt-1">Add SCORM packages, videos, documents to the master library</p>
              </CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="content-grid">
                {filtered.map((item, i) => {
                  const tc = CONTENT_TYPES[item.content_type] || CONTENT_TYPES.document;
                  return (
                    <motion.div key={item.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.3) }}>
                      <Card className="border shadow-none h-full group hover:border-primary/30 transition-colors">
                        <CardContent className="p-4 space-y-3">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center shrink-0", tc.color)}>
                                <tc.icon className="w-5 h-5" />
                              </div>
                              <div className="min-w-0">
                                <h3 className="text-sm font-semibold line-clamp-1">{item.title}</h3>
                                <p className="text-[10px] text-muted-foreground">{tc.label} &middot; {item.category}</p>
                              </div>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEditContent(item)}><Edit className="w-3.5 h-3.5 mr-2" />Edit</DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem className="text-red-600" onClick={() => handleDeleteContent(item.id)}><Trash2 className="w-3.5 h-3.5 mr-2" />Delete</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          {item.description && <p className="text-xs text-muted-foreground line-clamp-2">{item.description}</p>}
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {item.is_shared ? <Badge className="text-[9px] px-1.5 py-0 bg-green-50 text-green-700 border-green-200 gap-0.5"><Share2 className="w-2.5 h-2.5" />Shared</Badge> : <Badge className="text-[9px] px-1.5 py-0 bg-muted text-muted-foreground gap-0.5"><Lock className="w-2.5 h-2.5" />Private</Badge>}
                            {(item.tags || []).slice(0, 3).map(tag => <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">{tag}</span>)}
                          </div>
                          <div className="flex items-center justify-between text-[10px] text-muted-foreground pt-2 border-t">
                            <span>{formatSize(item.file_size_bytes)}</span>
                            <span>{new Date(item.created_at).toLocaleDateString()}</span>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          /* ═══ CERTIFICATE TEMPLATES ═══ */
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{certs.length} certificate templates</p>
              <Button size="sm" className="text-xs h-8 gap-1" onClick={() => { setEditCert(null); setCertForm({ name: "", description: "", layout: "landscape", background_color: "#FFFFFF", accent_color: "#1a56db", border_style: "elegant", signature_name: "", signature_title: "", footer_text: "", include_fields: ["learner_name", "course_name", "completion_date", "certificate_id"] }); setShowCertDialog(true); }} data-testid="create-cert-btn">
                <Plus className="w-3.5 h-3.5" /> New Certificate
              </Button>
            </div>

            {certs.length === 0 ? (
              <Card className="border shadow-none"><CardContent className="py-16 text-center">
                <Award className="w-12 h-12 mx-auto text-muted-foreground/20 mb-3" />
                <p className="text-sm font-medium">No certificate templates</p>
                <p className="text-xs text-muted-foreground mt-1">Create certificate layouts for course completions</p>
              </CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cert-grid">
                {certs.map((cert, i) => (
                  <motion.div key={cert.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.04, 0.3) }}>
                    <Card className="border shadow-none h-full group hover:border-primary/30 transition-colors overflow-hidden">
                      {/* Certificate Preview */}
                      <div className={cn("relative", cert.layout === "landscape" ? "aspect-[4/3]" : "aspect-[3/4]", "max-h-48 overflow-hidden")} style={{ backgroundColor: cert.background_color }}>
                        <div className="absolute inset-0 flex flex-col items-center justify-center p-6" style={{ borderColor: cert.accent_color }}>
                          <div className="w-8 h-8 rounded-full mb-2" style={{ backgroundColor: cert.accent_color, opacity: 0.2 }} />
                          <p className="text-[10px] font-bold tracking-wide uppercase opacity-60">Certificate of Completion</p>
                          <div className="w-16 h-px my-1.5" style={{ backgroundColor: cert.accent_color }} />
                          <p className="text-[8px] opacity-40">[Learner Name]</p>
                          <p className="text-[7px] opacity-30 mt-1">[Course Name]</p>
                        </div>
                        {cert.border_style !== "minimal" && (
                          <div className="absolute inset-2 border-2 rounded pointer-events-none" style={{ borderColor: `${cert.accent_color}40` }} />
                        )}
                      </div>
                      <CardContent className="p-4 space-y-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="text-sm font-semibold">{cert.name}</h3>
                            <p className="text-[10px] text-muted-foreground">{cert.layout} &middot; {cert.border_style}</p>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditCert(cert)}><Edit className="w-3.5 h-3.5 mr-2" />Edit</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-red-600" onClick={() => handleDeleteCert(cert.id)}><Trash2 className="w-3.5 h-3.5 mr-2" />Delete</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {cert.is_default && <Badge className="text-[9px] bg-amber-50 text-amber-700 border-amber-200">Default</Badge>}
                          <Badge variant="outline" className="text-[9px]">{(cert.include_fields || []).length} fields</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── CONTENT DIALOG ── */}
      <Dialog open={showContentDialog} onOpenChange={setShowContentDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="text-base">{editItem ? "Edit Content" : "Add Content"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Title</Label><Input value={contentForm.title} onChange={e => setContentForm({ ...contentForm, title: e.target.value })} className="h-8 text-sm" data-testid="content-title-input" /></div>
            <div><Label className="text-xs">Description</Label><Textarea value={contentForm.description} onChange={e => setContentForm({ ...contentForm, description: e.target.value })} rows={2} className="text-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Content Type</Label>
                <Select value={contentForm.content_type} onValueChange={v => setContentForm({ ...contentForm, content_type: v })}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CONTENT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Category</Label><Input value={contentForm.category} onChange={e => setContentForm({ ...contentForm, category: e.target.value })} className="h-8 text-sm" /></div>
            </div>
            <div><Label className="text-xs">File URL</Label><Input value={contentForm.file_url} onChange={e => setContentForm({ ...contentForm, file_url: e.target.value })} className="h-8 text-sm" placeholder="https://..." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">File Name</Label><Input value={contentForm.file_name} onChange={e => setContentForm({ ...contentForm, file_name: e.target.value })} className="h-8 text-sm" /></div>
              <div><Label className="text-xs">File Size (bytes)</Label><Input type="number" value={contentForm.file_size_bytes} onChange={e => setContentForm({ ...contentForm, file_size_bytes: parseInt(e.target.value) || 0 })} className="h-8 text-sm" /></div>
            </div>
            <div><Label className="text-xs">Tags (comma separated)</Label><Input value={contentForm.tags} onChange={e => setContentForm({ ...contentForm, tags: e.target.value })} className="h-8 text-sm" /></div>
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={contentForm.is_shared} onChange={e => setContentForm({ ...contentForm, is_shared: e.target.checked })} className="rounded" /> Share with all tenants</label>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowContentDialog(false)}>Cancel</Button>
            <Button size="sm" className="text-xs gap-1" onClick={handleSaveContent} disabled={saving} data-testid="save-content-btn">{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} {editItem ? "Update" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── CERTIFICATE DIALOG ── */}
      <Dialog open={showCertDialog} onOpenChange={setShowCertDialog}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-base">{editCert ? "Edit Certificate" : "New Certificate Template"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-xs">Name</Label><Input value={certForm.name} onChange={e => setCertForm({ ...certForm, name: e.target.value })} className="h-8 text-sm" data-testid="cert-name-input" /></div>
              <div className="col-span-2"><Label className="text-xs">Description</Label><Textarea value={certForm.description} onChange={e => setCertForm({ ...certForm, description: e.target.value })} rows={2} className="text-sm" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Layout</Label>
                <Select value={certForm.layout} onValueChange={v => setCertForm({ ...certForm, layout: v })}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="landscape">Landscape</SelectItem><SelectItem value="portrait">Portrait</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Background</Label><div className="flex gap-1"><Input type="color" value={certForm.background_color} onChange={e => setCertForm({ ...certForm, background_color: e.target.value })} className="h-8 w-10 p-0.5 cursor-pointer" /><Input value={certForm.background_color} onChange={e => setCertForm({ ...certForm, background_color: e.target.value })} className="h-8 text-xs flex-1" /></div></div>
              <div><Label className="text-xs">Accent Color</Label><div className="flex gap-1"><Input type="color" value={certForm.accent_color} onChange={e => setCertForm({ ...certForm, accent_color: e.target.value })} className="h-8 w-10 p-0.5 cursor-pointer" /><Input value={certForm.accent_color} onChange={e => setCertForm({ ...certForm, accent_color: e.target.value })} className="h-8 text-xs flex-1" /></div></div>
            </div>
            <div><Label className="text-xs">Border Style</Label>
              <Select value={certForm.border_style} onValueChange={v => setCertForm({ ...certForm, border_style: v })}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(BORDER_STYLES).map(([k, v]) => <SelectItem key={k} value={k}>{k.charAt(0).toUpperCase() + k.slice(1)} — {v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-2 block">Include Fields</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {CERT_FIELDS.map(f => (
                  <label key={f.key} className="flex items-center gap-2 text-xs p-1.5 rounded border hover:bg-muted/30 cursor-pointer">
                    <input type="checkbox" checked={certForm.include_fields.includes(f.key)} onChange={e => {
                      const fields = e.target.checked ? [...certForm.include_fields, f.key] : certForm.include_fields.filter(k => k !== f.key);
                      setCertForm({ ...certForm, include_fields: fields });
                    }} className="rounded" /> {f.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Signature Name</Label><Input value={certForm.signature_name} onChange={e => setCertForm({ ...certForm, signature_name: e.target.value })} className="h-8 text-sm" placeholder="John Smith" /></div>
              <div><Label className="text-xs">Signature Title</Label><Input value={certForm.signature_title} onChange={e => setCertForm({ ...certForm, signature_title: e.target.value })} className="h-8 text-sm" placeholder="Director of Training" /></div>
            </div>
            <div><Label className="text-xs">Footer Text</Label><Input value={certForm.footer_text} onChange={e => setCertForm({ ...certForm, footer_text: e.target.value })} className="h-8 text-sm" placeholder="This certificate is valid for 12 months..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowCertDialog(false)}>Cancel</Button>
            <Button size="sm" className="text-xs gap-1" onClick={handleSaveCert} disabled={saving} data-testid="save-cert-btn">{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} {editCert ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default ContentLibraryPage;
